/**
 * Team panel — who has access to this project, and how.
 *
 * Members and their roles, and invitations that have been sent but not accepted.
 * Public links and what a link shows live in the Share dialog, not here — this
 * panel is only about who is on the team.
 *
 * Role-aware throughout. The server gates invite/role/remove at owner+admin and
 * revoke at editor+, and it returns the caller's own role with the list — so a
 * viewer sees the roster read-only instead of buttons that 403 silently.
 *
 * Conventions: `getClassMap()` for styling, every string through `t()` with an
 * English `defaultValue`, `data-mol-id` on interactive elements.
 *
 * @module
 */

import { useCallback, useEffect, useState } from 'react'

import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Flex, Tooltip } from '@molecule/app-ui-react'

import { useIsMobile, useMediaQuery } from '../hooks/useMediaQuery.js'
import { upgradeLabel } from '../upgrade-copy.js'
import { onResourceInvalidated } from '../utilities/resource-invalidation.js'
import { Icon } from './Icon.js'
import { MoleculeSpinner } from './MoleculeSpinner.js'

/** Roles a member can hold. `owner` is the project creator and is not assignable. */
type TeamRole = 'owner' | 'admin' | 'editor' | 'viewer'

/** Roles the invite form and the role select offer. */
const ASSIGNABLE_ROLES: Exclude<TeamRole, 'owner'>[] = ['admin', 'editor', 'viewer']

interface TeamMember {
  id: string
  userId: string
  username: string
  email: string
  role: TeamRole
  acceptedAt: string | null
  createdAt: string
}

interface PendingInvite {
  id: string
  email: string
  role: TeamRole
  createdAt: string
  expiresAt: string
}

interface TeamData {
  /** The CALLER's role on this project — what the panel gates its controls on. */
  role: TeamRole
  /**
   * Seats the account pays for and how many are occupied. Absent on a plan
   * that does not sell seats, and on an older API that does not send it.
   */
  seats?: { total: number; used: number; perSeat: boolean }
  members: TeamMember[]
  invites: PendingInvite[]
}

const ROLE_COLORS: Record<string, string> = {
  owner: '#ffd43b',
  admin: '#cc5de8',
  editor: '#339af0',
  viewer: '#868e96',
}

/** Human label for a role, translated. */
function useRoleLabel(): (role: string) => string {
  const { t } = useTranslation()
  return (role: string) => {
    switch (role) {
      case 'owner':
        return t('team.role.owner.label', undefined, { defaultValue: 'Owner' })
      case 'admin':
        return t('team.role.admin.label', undefined, { defaultValue: 'Admin' })
      case 'editor':
        return t('team.role.editor.label', undefined, { defaultValue: 'Editor' })
      case 'viewer':
        return t('team.role.viewer.label', undefined, { defaultValue: 'Viewer' })
      default:
        return role
    }
  }
}

/**
 * What a role can do, what it can't, and whether it costs a seat.
 *
 * Picking a role was previously a bare "Admin / Editor / Viewer" dropdown, so
 * nothing in the product said that admin also means the secrets vault, raw
 * `env` values, SQL against the project database, backups/restore, data export,
 * API keys and team control — a materially different grant from editor.
 */
interface RoleCapability {
  /** What the role is allowed to do. */
  can: string
  /** The load-bearing half — what it deliberately can NOT do. */
  cannot: string
  /** Plan impact: editor + admin consume a builder seat, viewer never does. */
  seat: string
}

/**
 * Capability copy per assignable role, translated. Mirrors the server gates in
 * `api/src/App/router.ts` (`assertProjectRole` / `vaultControlGate`) and
 * `api/src/App/Resource/Team/index.ts` — keep the two in step.
 *
 * @returns A resolver from role name to its {@link RoleCapability} copy.
 */
function useRoleCapability(): (role: string) => RoleCapability | null {
  const { t } = useTranslation()
  return (role: string) => {
    switch (role) {
      case 'owner':
        return {
          can: t('team.role.owner.can', undefined, {
            defaultValue: 'Full access, including deleting the project.',
          }),
          cannot: t('team.role.owner.cannot', undefined, {
            defaultValue: 'The owner role cannot be assigned or removed.',
          }),
          seat: t('team.role.owner.seat', undefined, { defaultValue: 'Uses a builder seat.' }),
        }
      case 'admin':
        return {
          can: t('team.role.admin.can', undefined, {
            defaultValue:
              'Everything an editor can, plus secrets, database queries and backups, data export, API keys, and inviting or removing people.',
          }),
          cannot: t('team.role.admin.cannot', undefined, {
            defaultValue: 'Only the owner can delete the project.',
          }),
          seat: t('team.role.admin.seat', undefined, { defaultValue: 'Uses a builder seat.' }),
        }
      case 'editor':
        return {
          can: t('team.role.editor.can', undefined, {
            defaultValue:
              'Edit files, run the app, use Synthase, deploy, add domains, and read logs.',
          }),
          cannot: t('team.role.editor.cannot', undefined, {
            defaultValue: 'Cannot see secrets, query the database, or manage the team.',
          }),
          seat: t('team.role.editor.seat', undefined, { defaultValue: 'Uses a builder seat.' }),
        }
      case 'viewer':
        return {
          can: t('team.role.viewer.can', undefined, {
            defaultValue: 'Read the project — browse and search files.',
          }),
          cannot: t('team.role.viewer.cannot', undefined, {
            defaultValue: 'Cannot edit files, run the app, deploy, or see secrets.',
          }),
          seat: t('team.role.viewer.seat', undefined, {
            defaultValue: 'Free — does not use a builder seat.',
          }),
        }
      default:
        return null
    }
  }
}

/**
 * The capability disclosure for a role — rendered live under the role select so
 * the grant is visible at the moment it is chosen, not discovered later.
 *
 * @param root0 - Component props.
 * @param root0.role - The role whose capabilities to describe.
 * @param root0.molId - `data-mol-id` for the block.
 * @returns The rendered disclosure, or null for an unknown role.
 */
function RoleCapabilityNote({ role, molId }: { role: string; molId: string }) {
  const capability = useRoleCapability()(role)
  if (!capability) return null

  const line = (icon: 'check' | 'x-mark' | 'people', text: string, color: string) => (
    <Flex align="start" gap="xs" style={{ marginTop: 4 }}>
      <span style={{ color, display: 'flex', flexShrink: 0, marginTop: 1 }}>
        <Icon name={icon} size={12} aria-hidden="true" />
      </span>
      <span>{text}</span>
    </Flex>
  )

  return (
    <div
      data-mol-id={molId}
      style={{
        marginTop: 8,
        padding: '8px 10px',
        borderRadius: 6,
        fontSize: 11.5,
        lineHeight: 1.45,
        background: 'color-mix(in srgb, var(--color-border) 25%, transparent)',
        border: '1px solid var(--color-border)',
      }}
    >
      {line('check', capability.can, 'var(--color-success, #2f9e44)')}
      {line('x-mark', capability.cannot, 'var(--color-error-text, #e03131)')}
      {line('people', capability.seat, 'inherit')}
    </div>
  )
}

/** The error message an API rejection carries, if it has one. */
function errorMessageOf(error: unknown): string | null {
  const e = error as { response?: { data?: { error?: unknown } } } | null | undefined
  const message = e?.response?.data?.error
  return typeof message === 'string' && message ? message : null
}

/** Whether an API rejection is the plan/tier gate (the server says so). */
function isUpgradeError(error: unknown): boolean {
  const e = error as { response?: { data?: { upgrade?: unknown } } } | null | undefined
  return e?.response?.data?.upgrade === true
}

/** A small section heading inside the panel. */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 12, opacity: 0.55, marginBottom: 6 }}>{children}</div>
}

/**
 * The Team panel.
 *
 * @param root0 - Component props.
 * @param root0.projectId - Project whose access to manage.
 * @returns The rendered panel.
 */
export function TeamPanel({ projectId }: { projectId: string }) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  const roleLabel = useRoleLabel()
  const capabilityOf = useRoleCapability()
  /** One-line grant summary for a role — the tooltip body on a role badge. */
  const roleSummary = (role: string): string => {
    const capability = capabilityOf(role)
    return capability
      ? `${capability.can} ${capability.cannot} ${capability.seat}`
      : roleLabel(role)
  }
  // 16px input font on phones/touch — iOS zooms any focusable field below 16px.
  const isMobile = useIsMobile()
  const coarse = useMediaQuery('(pointer: coarse)')

  const [data, setData] = useState<TeamData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [upgradeNeeded, setUpgradeNeeded] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<Exclude<TeamRole, 'owner'>>('editor')
  const [inviting, setInviting] = useState(false)

  const callerRole: TeamRole = data?.role ?? 'viewer'
  const canManageMembers = callerRole === 'owner' || callerRole === 'admin'

  const fetchTeam = useCallback(async () => {
    try {
      const res = await http.get<{ data: TeamData }>(`/projects/${projectId}/team`)
      setData(res.data?.data ?? null)
      setError(null)
    } catch (_error) {
      setError(t('team.loadError', undefined, { defaultValue: 'Could not load the team.' }))
    }
    setLoading(false)
  }, [http, projectId, t])

  useEffect(() => {
    void fetchTeam()
  }, [fetchTeam])

  // Live cross-client refresh: another member's invite/role-change/removal
  // lands while this panel is OPEN — before this, the list only refreshed on
  // mount and after this user's own mutations.
  useEffect(() => onResourceInvalidated('team', () => void fetchTeam()), [fetchTeam])

  const handleInvite = useCallback(async () => {
    const email = inviteEmail.trim()
    if (!email) return
    setInviting(true)
    setError(null)
    setUpgradeNeeded(false)
    setNotice(null)
    try {
      const res = await http.post<{ data?: { emailSent?: boolean } }>(
        `/projects/${projectId}/team/invite`,
        { email, role: inviteRole },
      )
      setInviteEmail('')
      setNotice(
        res.data?.data?.emailSent === false
          ? t(
              'team.invite.sentNoEmail',
              { email },
              {
                defaultValue:
                  'Invited {{email}}. We could not send the email — they can accept from their dashboard.',
              },
            )
          : t(
              'team.invite.sent',
              { email },
              {
                defaultValue: 'Invited {{email}}. They have an email with a link to join.',
              },
            ),
      )
      await fetchTeam()
    } catch (err) {
      setUpgradeNeeded(isUpgradeError(err))
      setError(
        errorMessageOf(err) ??
          t('team.invite.failed', undefined, { defaultValue: 'Could not send that invitation.' }),
      )
    }
    setInviting(false)
  }, [http, projectId, inviteEmail, inviteRole, fetchTeam, t])

  const handleRemove = useCallback(
    async (memberId: string) => {
      setError(null)
      setNotice(null)
      try {
        await http.delete(`/projects/${projectId}/team/${memberId}`)
        await fetchTeam()
      } catch (err) {
        setError(
          errorMessageOf(err) ??
            t('team.remove.failed', undefined, { defaultValue: 'Could not remove that member.' }),
        )
      }
    },
    [http, projectId, fetchTeam, t],
  )

  const handleRoleChange = useCallback(
    async (memberId: string, role: string) => {
      setError(null)
      setUpgradeNeeded(false)
      setNotice(null)
      try {
        await http.patch(`/projects/${projectId}/team/${memberId}`, { role })
        await fetchTeam()
      } catch (err) {
        setUpgradeNeeded(isUpgradeError(err))
        setError(
          errorMessageOf(err) ??
            t('team.role.failed', undefined, { defaultValue: 'Could not change that role.' }),
        )
        // Re-read so the select snaps back to what the server actually holds
        // instead of showing a change that did not happen.
        await fetchTeam()
      }
    },
    [http, projectId, fetchTeam, t],
  )

  const handleCancelInvite = useCallback(
    async (inviteId: string) => {
      setError(null)
      setNotice(null)
      try {
        await http.delete(`/projects/${projectId}/team/invites/${inviteId}`)
        await fetchTeam()
      } catch (err) {
        setError(
          errorMessageOf(err) ??
            t('team.invite.cancelFailed', undefined, {
              defaultValue: 'Could not cancel that invitation.',
            }),
        )
      }
    },
    [http, projectId, fetchTeam, t],
  )

  const controlStyle = {
    height: 32,
    boxSizing: 'border-box' as const,
    fontSize: 12,
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border-secondary)',
    borderRadius: 4,
    color: 'inherit',
    padding: '2px 6px',
  }

  if (loading) {
    return (
      <Flex align="center" justify="center" className={cm.sp('py', 6)}>
        <MoleculeSpinner />
      </Flex>
    )
  }

  return (
    // Fills the PanelModal (the DeployPanel/LogsPanel idiom). The modal chrome
    // owns the "Team" title — no duplicate heading here.
    <div
      data-mol-id="team-panel"
      style={{ padding: 16, height: '100%', boxSizing: 'border-box', overflow: 'auto' }}
    >
      {error && (
        <div
          data-mol-id="team-error"
          style={{
            padding: 10,
            marginBottom: 12,
            borderRadius: 8,
            fontSize: 13,
            lineHeight: 1.4,
            color: 'var(--color-error-text, #b81c00)',
            background: 'color-mix(in srgb, var(--color-error) 12%, transparent)',
            border: '1px solid color-mix(in srgb, var(--color-error) 30%, transparent)',
          }}
        >
          {error}
          {upgradeNeeded && (
            <div style={{ marginTop: 8 }}>
              {/* A plain anchor, styled as the design-system button: `/pricing`
                  is intercepted by AuthModalMount and opens the upgrade modal in
                  place, which `window.open` escaped — it tore the user out of
                  the IDE into a new tab. `upgrade.viewPlans` resolves to "View
                  plans", so the label uses the key that means Upgrade. */}
              <a
                data-mol-id="team-upgrade"
                href="/pricing"
                className={cm.cn(
                  cm.button({ color: 'primary', size: 'sm' }),
                  cm.touchTargetCompact,
                )}
                style={{ textDecoration: 'none' }}
              >
                {upgradeLabel()}
              </a>
            </div>
          )}
        </div>
      )}

      {notice && (
        <div
          data-mol-id="team-notice"
          style={{
            padding: 10,
            marginBottom: 12,
            borderRadius: 8,
            fontSize: 13,
            lineHeight: 1.4,
            color: 'var(--color-primary, #4070e0)',
            background: 'color-mix(in srgb, var(--color-primary) 12%, transparent)',
            border: '1px solid color-mix(in srgb, var(--color-primary) 30%, transparent)',
          }}
        >
          {notice}
        </div>
      )}

      {/* Members */}
      <div style={{ marginBottom: 16 }}>
        <SectionLabel>{t('team.members', undefined, { defaultValue: 'Members' })}</SectionLabel>
        {/* What the subscription covers, before the owner runs into it. A seat
            cap is only discoverable by hitting it otherwise. */}
        {data?.seats?.perSeat && (
          <div
            data-mol-id="team-seats"
            style={{ fontSize: 12, opacity: 0.7, marginTop: -4, marginBottom: 6 }}
          >
            {t(
              'team.seats.inUse',
              { used: data.seats.used, total: data.seats.total },
              { defaultValue: '{{used}} of {{total}} seats in use' },
            )}
          </div>
        )}
        {(data?.members ?? []).length === 0 && (
          <div style={{ fontSize: 13, opacity: 0.7, padding: '4px 0' }}>
            {t('team.members.empty', undefined, {
              defaultValue: 'Just you so far. Invite someone below.',
            })}
          </div>
        )}
        {(data?.members ?? []).map((member) => (
          <Flex
            key={member.id}
            align="center"
            justify="between"
            wrap="wrap"
            gap="xs"
            style={{ padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}
          >
            <Flex align="center" gap="xs">
              <span style={{ fontWeight: 600, fontSize: 13 }}>
                {member.username || member.email}
              </span>
              {/* The roster says the same thing the invite form does: hovering a
                  role badge explains the grant instead of leaving "Admin" to be
                  guessed at. */}
              <Tooltip content={roleSummary(member.role)} placement="top">
                <span
                  data-mol-id={`team-member-role-${member.id}`}
                  tabIndex={0}
                  style={{
                    fontSize: 10,
                    background: `${ROLE_COLORS[member.role] ?? '#868e96'}22`,
                    color: ROLE_COLORS[member.role] ?? '#868e96',
                    padding: '1px 6px',
                    borderRadius: 4,
                    fontWeight: 600,
                    cursor: 'help',
                  }}
                >
                  {roleLabel(member.role)}
                </span>
              </Tooltip>
            </Flex>
            {member.role !== 'owner' && canManageMembers && (
              <Flex align="center" gap="xs">
                <select
                  data-mol-id={`team-role-select-${member.id}`}
                  value={member.role}
                  aria-label={t('team.role.change', undefined, { defaultValue: 'Change role' })}
                  onChange={(e) => void handleRoleChange(member.id, e.target.value)}
                  style={controlStyle}
                >
                  {ASSIGNABLE_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {roleLabel(role)}
                    </option>
                  ))}
                </select>
                {/* Destructive action → `error`, per molecule/DESIGN.md's CTA
                    color table (Delete/Stop = error). */}
                <button
                  type="button"
                  data-mol-id={`team-remove-${member.id}`}
                  onClick={() => void handleRemove(member.id)}
                  className={cm.cn(
                    cm.button({ variant: 'solid', color: 'error', size: 'md' }),
                    cm.touchTargetCompact,
                  )}
                >
                  {t('team.remove', undefined, { defaultValue: 'Remove' })}
                </button>
              </Flex>
            )}
          </Flex>
        ))}
      </div>

      {/* Pending invitations */}
      {(data?.invites?.length ?? 0) > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionLabel>
            {t('team.invites.pending', undefined, { defaultValue: 'Invited, not joined yet' })}
          </SectionLabel>
          {data!.invites.map((invite) => (
            <Flex
              key={invite.id}
              align="center"
              justify="between"
              gap="xs"
              wrap="wrap"
              style={{ padding: '4px 0', fontSize: 13 }}
            >
              <span style={{ opacity: 0.8 }} data-mol-id={`team-invite-${invite.id}`}>
                {invite.email}
              </span>
              <Flex align="center" gap="xs">
                <Tooltip content={roleSummary(invite.role)} placement="top">
                  <span style={{ fontSize: 11, opacity: 0.55, cursor: 'help' }} tabIndex={0}>
                    {roleLabel(invite.role)}
                  </span>
                </Tooltip>
                {canManageMembers && (
                  // Quiet secondary action beside the invite → outline.
                  <button
                    type="button"
                    data-mol-id={`team-invite-cancel-${invite.id}`}
                    onClick={() => void handleCancelInvite(invite.id)}
                    className={cm.cn(
                      cm.button({ variant: 'outline', size: 'md' }),
                      cm.touchTargetCompact,
                    )}
                  >
                    {t('team.invite.cancel', undefined, { defaultValue: 'Cancel' })}
                  </button>
                )}
              </Flex>
            </Flex>
          ))}
        </div>
      )}

      {/* Invite form */}
      {canManageMembers && (
        <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
          <SectionLabel>
            {t('team.invite.heading', undefined, { defaultValue: 'Invite someone' })}
          </SectionLabel>
          {/* Wraps only when the row can't fit (phones): the email input takes
              its own full-width line; role + Invite share the next one. */}
          <Flex align="center" gap="xs" wrap="wrap">
            <input
              type="email"
              data-mol-id="team-invite-email"
              placeholder={t('team.invite.emailPlaceholder', undefined, {
                defaultValue: 'Email address',
              })}
              aria-label={t('team.invite.emailPlaceholder', undefined, {
                defaultValue: 'Email address',
              })}
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleInvite()
              }}
              style={{
                flex: '1 1 200px',
                minWidth: 0,
                height: isMobile ? 40 : 32,
                boxSizing: 'border-box',
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid var(--color-border-secondary)',
                background: 'var(--color-surface)',
                color: 'inherit',
                // 16px on phones/touch — iOS zooms any focusable field below 16px.
                fontSize: isMobile || coarse ? 16 : 13,
                outline: 'none',
              }}
            />
            <select
              data-mol-id="team-invite-role"
              value={inviteRole}
              aria-label={t('team.invite.roleLabel', undefined, { defaultValue: 'Role' })}
              onChange={(e) => setInviteRole(e.target.value as Exclude<TeamRole, 'owner'>)}
              style={{ ...controlStyle, height: isMobile ? 40 : 32 }}
            >
              {ASSIGNABLE_ROLES.map((role) => (
                <option key={role} value={role}>
                  {roleLabel(role)}
                </option>
              ))}
            </select>
            {/* Same height as the email input + role select it shares the row
                with. `size="sm"` alone renders 26px against their 32px, and
                touchTargetCompact's coarse-pointer floor is 36px against their
                40px — a visibly ragged row at both breakpoints. */}
            <Button
              data-mol-id="team-invite-submit"
              size="sm"
              className={cm.touchTargetCompact}
              style={{ height: isMobile ? 40 : 32 }}
              onClick={() => void handleInvite()}
              disabled={inviting || !inviteEmail.trim()}
            >
              {inviting ? (
                <MoleculeSpinner size="sm" />
              ) : (
                t('team.invite.submit', undefined, { defaultValue: 'Invite' })
              )}
            </Button>
          </Flex>
          {/* What the selected role actually grants — live on the select, so the
              difference between admin and editor is visible at the moment the
              choice is made rather than discovered after the invite lands. */}
          <RoleCapabilityNote role={inviteRole} molId="team-invite-role-capabilities" />
          <div style={{ fontSize: 11.5, opacity: 0.6, marginTop: 6, lineHeight: 1.4 }}>
            {t('team.invite.hint', undefined, {
              defaultValue: 'They get an email with a link. They join when they accept it.',
            })}
          </div>
        </div>
      )}

      {!canManageMembers && (
        <div style={{ fontSize: 11.5, opacity: 0.6, marginTop: 16, lineHeight: 1.4 }}>
          {t('team.readOnly', undefined, {
            defaultValue: 'Only owners and admins can change who has access.',
          })}
        </div>
      )}
    </div>
  )
}
